import { useLayoutEffect, type RefObject } from "react";

const FOOTER_HEIGHT_PROPERTY = "--_picker-footer-height";

const publishFooterHeight = (footer: HTMLElement, container: HTMLElement) => {
    container.style.setProperty(
        FOOTER_HEIGHT_PROPERTY,
        `${footer.offsetHeight}px`,
    );
};

/**
 * Re-measures the footer pinned inside `list`, for a caller about to scroll a
 * row into view. A searchable root's popup can mount hidden (Base UI
 * force-mounts it on the trigger's mousedown or focus), where the footer
 * measures 0, and its open reveal runs a frame before the ResizeObserver
 * reports the real height.
 */
export const syncPickerFooterScrollPadding = (list: HTMLElement | null) => {
    const footer = list?.querySelector<HTMLElement>(
        ':scope > [data-picker-part="footer"]',
    );
    if (list && footer) {
        publishFooterHeight(footer, list);
    }
};

/**
 * Publishes a footer's height on the element around it. Inside `Picker.List`
 * the footer is pinned over the rows, and the list's `scroll-padding-bottom`
 * (Picker.module.scss) adds this height so a keyboard-highlighted row is
 * revealed above the footer rather than under it. Re-measured whenever the
 * footer resizes; `offsetHeight` ignores the popup's opening scale transform.
 */
export const usePickerFooterScrollPadding = (
    footerRef: RefObject<HTMLDivElement | null>,
) => {
    useLayoutEffect(() => {
        const footer = footerRef.current;
        const container = footer?.parentElement;
        if (!footer || !container) {
            return;
        }
        const measure = () => publishFooterHeight(footer, container);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(footer);
        return () => {
            observer.disconnect();
            container.style.removeProperty(FOOTER_HEIGHT_PROPERTY);
        };
    }, [footerRef]);
};
